// src/pages/VotacionesAvisosResidente.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaBullhorn, FaBell, FaInfoCircle, FaHistory } from 'react-icons/fa';
import { collection, onSnapshot, orderBy, query, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import VotacionCard from '../components/VotacionCard';
import { countEligibleVoters } from '../utils/countEligibleVoters';
import { useRestriccionMorosidad } from '../hooks/useRestriccionMorosidad';
import {
    calcularEstadoVotacionEfectivo,
    esResidenteHabilitadoParaVotacion,
    normalizarFechaFinVotacion,
} from '../utils/votacionesUtils';
import './VotacionesAvisosResidente.css';
import './VotacionesAvisosResidenteMobile.css'; // Import explicit mobile overrides

const TAB_KEYS = {
    VOTACIONES: 'votaciones',
    AVISOS: 'avisos',
};

const CATEGORIAS_FILTRO = ['Todas', 'Comunidad', 'Vigilancia/Seguridad', 'Finanzas (Ingresos/Egresos)', 'Administración'];

const statusPriority = {
    activa: 0,
    programada: 1,
    vencida: 2,
};

const VotacionesAvisosResidente = () => {
    const { userData, currentUser, casaActiva, misCasas } = useAuth();
    const { showNotification } = useNotification();
    const { userDebt } = useRestriccionMorosidad();
    const location = useLocation();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

    const esResidenteMoroso = Boolean(userDebt?.moroso);

    const [activeTab, setActiveTab] = useState(TAB_KEYS.VOTACIONES);
    const [categoriaFiltro, setCategoriaFiltro] = useState('Todas');
    const [votaciones, setVotaciones] = useState([]);
    const [avisos, setAvisos] = useState([]);
    const [loadingVotaciones, setLoadingVotaciones] = useState(true);
    const [loadingAvisos, setLoadingAvisos] = useState(true);
    const [totalParticipantes, setTotalParticipantes] = useState(0);
    const [readStatus, setReadStatus] = useState({});
    const [toggleLoading, setToggleLoading] = useState({});
    const [showExpired, setShowExpired] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const requestedTab = params.get('tab') || location.state?.tab;
        const normalized = requestedTab ? String(requestedTab).toLowerCase() : null;
        if (normalized && Object.values(TAB_KEYS).includes(normalized) && normalized !== activeTab) {
            setActiveTab(normalized);
        }
    }, [location.search, location.state, activeTab]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setVotaciones([]);
            setAvisos([]);
            setLoadingVotaciones(false);
            setLoadingAvisos(false);
            return undefined;
        }

        setLoadingVotaciones(true);
        setLoadingAvisos(true);

        const votacionesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'votaciones');
        const unsubscribeVotaciones = onSnapshot(
            query(votacionesRef, orderBy('fechaCreacion', 'desc')),
            (snapshot) => {
                const items = snapshot.docs.map((docSnap) => {
                    const data = docSnap.data();
                    const estadoCalculado = calcularEstadoVotacionEfectivo(data);
                    return {
                        id: docSnap.id,
                        ...data,
                        estadoCalculado,
                        fechaLimite: normalizarFechaFinVotacion(data.fechaFin),
                        fechaPublicacion: normalizarFecha(data.fechaCreacion),
                    };
                }).sort((a, b) => {
                    const prioDiff = (statusPriority[a.estadoCalculado] ?? 99) - (statusPriority[b.estadoCalculado] ?? 99);
                    if (prioDiff !== 0) return prioDiff;
                    return obtenerTiempoVotacion(b) - obtenerTiempoVotacion(a);
                });
                setVotaciones(items);
                setLoadingVotaciones(false);
            },
            (error) => {
                console.error('VotacionesAvisosResidente: error al obtener votaciones', error);
                setVotaciones([]);
                setLoadingVotaciones(false);
            }
        );

        const avisosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'comunicados');
        const unsubscribeAvisos = onSnapshot(
            query(avisosRef, orderBy('fecha', 'desc')),
            (snapshot) => {
                const items = snapshot.docs.map((docSnap) => {
                    const data = docSnap.data();
                    return {
                        id: docSnap.id,
                        ...data,
                        fechaPublicacion: normalizarFecha(data.fecha),
                    };
                });
                setAvisos(items);
                setLoadingAvisos(false);
            },
            (error) => {
                console.error('VotacionesAvisosResidente: error al obtener avisos', error);
                setAvisos([]);
                setLoadingAvisos(false);
            }
        );

        return () => {
            unsubscribeVotaciones();
            unsubscribeAvisos();
        };
    }, [fraccionamientoId]);

    const isLoading = loadingVotaciones || loadingAvisos;

    const userUid = currentUser?.uid || userData?.uid || null;

    useEffect(() => {
        if (!fraccionamientoId) {
            setTotalParticipantes(0);
            return;
        }

        let cancelled = false;

        const fetchMeta = async () => {
            try {
                const fraccSnap = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (cancelled) {
                    return;
                }

                if (fraccSnap.exists()) {
                    const explicitTarget = inferTotalParticipantes(fraccSnap.data());
                    if (explicitTarget > 0) {
                        setTotalParticipantes(explicitTarget);
                        return;
                    }
                }

                const fallbackTarget = await countEligibleVoters(fraccionamientoId);
                if (!cancelled) {
                    setTotalParticipantes(fallbackTarget);
                }
            } catch (error) {
                console.error('VotacionesAvisosResidente: error al obtener fraccionamiento', error);
                const fallbackTarget = await countEligibleVoters(fraccionamientoId);
                if (!cancelled) {
                    setTotalParticipantes(fallbackTarget);
                }
            }
        };

        fetchMeta();
        return () => { cancelled = true; };
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId || !userUid || avisos.length === 0) {
            setReadStatus({});
            return;
        }

        let cancelled = false;

        const fetchReadStatus = async () => {
            try {
                const entries = await Promise.all(avisos.map(async (aviso) => {
                    const lecturaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'comunicados', aviso.id, 'lecturas', userUid);
                    const lecturaSnap = await getDoc(lecturaRef);
                    return [aviso.id, lecturaSnap.exists()];
                }));
                if (!cancelled) {
                    setReadStatus(Object.fromEntries(entries));
                }
            } catch (error) {
                console.error('VotacionesAvisosResidente: error al obtener lecturas de avisos', error);
                if (!cancelled) {
                    setReadStatus({});
                }
            }
        };

        fetchReadStatus();

        return () => { cancelled = true; };
    }, [avisos, fraccionamientoId, userUid]);

    const avisosVisibles = useMemo(() => {
        return avisos.filter((aviso) => {
            if (aviso.tipoDestinatarios === 'morosos' || aviso.enviarSoloMorosos || aviso.soloMorosos) {
                return esResidenteMoroso;
            }
            if (aviso.tipoDestinatarios === 'personalizado' || (Array.isArray(aviso.casasHabilitadas) && aviso.casasHabilitadas.length > 0)) {
                return esResidenteHabilitadoParaVotacion(aviso, userData, casaActiva, misCasas);
            }
            return true;
        });
    }, [avisos, esResidenteMoroso, userData, casaActiva, misCasas]);

    const unreadCount = useMemo(() => {
        if (!avisosVisibles.length) return 0;
        return avisosVisibles.reduce((total, aviso) => total + (readStatus[aviso.id] ? 0 : 1), 0);
    }, [avisosVisibles, readStatus]);

    const votacionesPermitidas = useMemo(() => {
        return votaciones.filter((v) => esResidenteHabilitadoParaVotacion(v, userData, casaActiva, misCasas));
    }, [votaciones, userData, casaActiva, misCasas]);

    const votacionesFiltradas = useMemo(() => {
        return votacionesPermitidas.filter((v) => {
            if (categoriaFiltro === 'Todas') return true;
            return (v.categoria || 'Comunidad') === categoriaFiltro;
        });
    }, [votacionesPermitidas, categoriaFiltro]);

    const handleToggleAviso = async (avisoId, isRead) => {
        if (!fraccionamientoId || !userUid) {
            showNotification('No se pudo actualizar el estado del aviso.', 'error');
            return;
        }

        const lecturaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'comunicados', avisoId, 'lecturas', userUid);

        setToggleLoading((prev) => ({ ...prev, [avisoId]: true }));
        try {
            if (isRead) {
                await deleteDoc(lecturaRef);
                setReadStatus((prev) => ({ ...prev, [avisoId]: false }));
                showNotification('El aviso se marcó como no leído.', 'info');
            } else {
                const userCasa = String(
                    casaActiva?.numero ||
                    casaActiva?.numeroCasa ||
                    userData?.numeroCasa ||
                    userData?.casa ||
                    userData?.casaNumero ||
                    userData?.lote ||
                    userData?.numero ||
                    (Array.isArray(misCasas) && misCasas[0] ? (misCasas[0].numero || misCasas[0].numeroCasa) : '') ||
                    ''
                ).trim() || 'Sin número';

                const actorNombre = (
                    userData?.nombreCompleto ||
                    userData?.nombre ||
                    userData?.nombres ||
                    currentUser?.displayName ||
                    'Residente'
                ).trim();

                await setDoc(lecturaRef, {
                    leidoEn: serverTimestamp(),
                    casa: userCasa,
                    numeroCasa: userCasa,
                    nombre: actorNombre,
                    usuarioId: userUid
                }, { merge: true });
                setReadStatus((prev) => ({ ...prev, [avisoId]: true }));
                showNotification('Aviso marcado como leído.', 'success');
            }
        } catch (error) {
            console.error('VotacionesAvisosResidente: error al actualizar lectura', error);
            showNotification('No se pudo guardar el cambio. Inténtalo de nuevo.', 'error');
        } finally {
            setToggleLoading((prev) => ({ ...prev, [avisoId]: false }));
        }
    };

    if (!fraccionamientoId) {
        return (
            <div className="resident-va-page">
                <div className="resident-va-empty">
                    No se pudo identificar tu fraccionamiento. Intenta iniciar sesión nuevamente.
                </div>
            </div>
        );
    }

    return (
        <div className="resident-va-page">
            <section className="resident-va-hero">
                <div className="resident-va-hero-icon">
                    <FaBullhorn />
                </div>
                <div>
                    <h1>Votaciones y Avisos</h1>
                    <p>Participa en las decisiones de tu comunidad y mantente informado con los avisos más recientes.</p>
                </div>
            </section>

            <div className="resident-va-tabs">
                <button
                    type="button"
                    className={activeTab === TAB_KEYS.VOTACIONES ? 'active' : ''}
                    onClick={() => setActiveTab(TAB_KEYS.VOTACIONES)}
                >
                    Votaciones Activas
                </button>
                <button
                    type="button"
                    className={activeTab === TAB_KEYS.AVISOS ? 'active' : ''}
                    onClick={() => setActiveTab(TAB_KEYS.AVISOS)}
                >
                    Avisos
                    {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
                </button>

                {activeTab === TAB_KEYS.VOTACIONES && (
                    <button
                        type="button"
                        className={`btn-history-toggle web-only ${showExpired ? 'active' : ''}`}
                        onClick={() => setShowExpired(!showExpired)}
                        title={showExpired ? "Ocultar historial" : "Ver votaciones vencidas"}
                    >
                        <FaHistory />
                        <span className="btn-text">{showExpired ? "Ver Activas" : "Historial"}</span>
                    </button>
                )}
            </div>

            {/* Mantener botón original SOLO para móvil (se oculta en web vía CSS) */}
            <button
                type="button"
                className={`btn-history-toggle mobile-only ${showExpired ? 'active' : ''}`}
                onClick={() => setShowExpired(!showExpired)}
            >
                <FaHistory />
            </button>

            {/* Barra de Filtros Zen por Categoría para Votaciones */}
            {activeTab === TAB_KEYS.VOTACIONES && votacionesPermitidas.length > 0 && (
                <div className="votaciones-categorias-bar" style={{ marginTop: '16px', marginBottom: '16px' }}>
                    {CATEGORIAS_FILTRO.map((cat) => {
                        const isActive = categoriaFiltro === cat;
                        const count = cat === 'Todas'
                            ? votacionesPermitidas.length
                            : votacionesPermitidas.filter((v) => (v.categoria || 'Comunidad') === cat).length;
                        return (
                            <button
                                key={cat}
                                type="button"
                                className={`votaciones-cat-pill ${isActive ? 'active' : ''}`}
                                onClick={() => setCategoriaFiltro(cat)}
                            >
                                {cat} {count > 0 && <span className="cat-count">({count})</span>}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="resident-va-panel">
                {isLoading ? (
                    <div className="resident-va-empty">Cargando información...</div>
                ) : activeTab === TAB_KEYS.VOTACIONES ? (
                    <VotacionesList
                        votaciones={votacionesFiltradas}
                        totalParticipantes={totalParticipantes}
                        showExpired={showExpired}
                    />
                ) : (
                    <AvisosList
                        avisos={avisosVisibles}
                        readStatus={readStatus}
                        onToggleRead={handleToggleAviso}
                        toggleLoading={toggleLoading}
                    />
                )}
            </div>
        </div>
    );
};

export default VotacionesAvisosResidente;

function VotacionesList({ votaciones, totalParticipantes, showExpired }) {
    const [paginaActivas, setPaginaActivas] = useState(1);
    const [paginaCaducadas, setPaginaCaducadas] = useState(1);
    const ITEMS_PER_PAGE = 5;

    if (!votaciones.length) {
        return <div className="resident-va-empty">Aún no hay votaciones registradas en tu fraccionamiento.</div>;
    }

    const activas = votaciones
        .filter((v) => v.estadoCalculado !== 'vencida')
        .sort((a, b) => obtenerTiempoVotacion(b) - obtenerTiempoVotacion(a));
    const caducadas = votaciones
        .filter((v) => v.estadoCalculado === 'vencida')
        .sort((a, b) => obtenerTiempoVotacion(b) - obtenerTiempoVotacion(a));

    const totalPaginasActivas = Math.ceil(activas.length / ITEMS_PER_PAGE) || 1;
    const activasPaginadas = activas.slice((paginaActivas - 1) * ITEMS_PER_PAGE, paginaActivas * ITEMS_PER_PAGE);

    const totalPaginasCaducadas = Math.ceil(caducadas.length / ITEMS_PER_PAGE) || 1;
    const caducadasPaginadas = caducadas.slice((paginaCaducadas - 1) * ITEMS_PER_PAGE, paginaCaducadas * ITEMS_PER_PAGE);

    return (
        <div className="resident-va-vote-groups">
            {activas.length > 0 ? (
                <section className="resident-va-vote-group">
                    <h2>Votaciones activas y próximas ({activas.length})</h2>
                    <div className="resident-va-vote-group-list">
                        {activasPaginadas.map((votacion) => (
                            <VotacionCard
                                key={votacion.id}
                                votacion={votacion}
                                totalParticipantes={totalParticipantes}
                            />
                        ))}
                    </div>
                    {totalPaginasActivas > 1 && (
                        <div className="votaciones-paginacion" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                            <button type="button" className="btn-zen-action" onClick={() => setPaginaActivas(p => Math.max(1, p - 1))} disabled={paginaActivas === 1} style={{ opacity: paginaActivas === 1 ? 0.4 : 1, cursor: paginaActivas === 1 ? 'not-allowed' : 'pointer' }}>← Anterior</button>
                            <span style={{ fontSize: '0.88rem', fontWeight: '600', color: '#64748b' }}>Página {paginaActivas} de {totalPaginasActivas}</span>
                            <button type="button" className="btn-zen-action" onClick={() => setPaginaActivas(p => Math.min(totalPaginasActivas, p + 1))} disabled={paginaActivas === totalPaginasActivas} style={{ opacity: paginaActivas === totalPaginasActivas ? 0.4 : 1, cursor: paginaActivas === totalPaginasActivas ? 'not-allowed' : 'pointer' }}>Siguiente →</button>
                        </div>
                    )}
                </section>
            ) : (
                <div className="resident-va-info-card">
                    Actualmente no hay votaciones activas. Revisa el historial caducado o vuelve más tarde.
                </div>
            )}

            {showExpired && caducadas.length > 0 && (
                <section className="resident-va-vote-group">
                    <h2>Votaciones caducadas ({caducadas.length})</h2>
                    <div className="resident-va-vote-group-list">
                        {caducadasPaginadas.map((votacion) => (
                            <VotacionCard
                                key={`${votacion.id}-caducada`}
                                votacion={votacion}
                                totalParticipantes={totalParticipantes}
                            />
                        ))}
                    </div>
                    {totalPaginasCaducadas > 1 && (
                        <div className="votaciones-paginacion" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                            <button type="button" className="btn-zen-action" onClick={() => setPaginaCaducadas(p => Math.max(1, p - 1))} disabled={paginaCaducadas === 1} style={{ opacity: paginaCaducadas === 1 ? 0.4 : 1, cursor: paginaCaducadas === 1 ? 'not-allowed' : 'pointer' }}>← Anterior</button>
                            <span style={{ fontSize: '0.88rem', fontWeight: '600', color: '#64748b' }}>Página {paginaCaducadas} de {totalPaginasCaducadas}</span>
                            <button type="button" className="btn-zen-action" onClick={() => setPaginaCaducadas(p => Math.min(totalPaginasCaducadas, p + 1))} disabled={paginaCaducadas === totalPaginasCaducadas} style={{ opacity: paginaCaducadas === totalPaginasCaducadas ? 0.4 : 1, cursor: paginaCaducadas === totalPaginasCaducadas ? 'not-allowed' : 'pointer' }}>Siguiente →</button>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

function AvisosList({ avisos, readStatus, onToggleRead, toggleLoading }) {
    if (!avisos.length) {
        return <div className="resident-va-empty">Tu administrador aún no publica avisos.</div>;
    }

    return (
        <div className="resident-va-list">
            {avisos.map((aviso) => {
                const iconMeta = obtenerMetaAviso(aviso.categoria);
                const isRead = !!readStatus[aviso.id];
                const isToggling = !!toggleLoading[aviso.id];
                return (
                    <article key={aviso.id} className={`resident-va-notice ${isRead ? 'read' : 'unread'}`}>
                        <div className={`resident-va-notice-icon ${iconMeta.className}`}>
                            {iconMeta.icon}
                        </div>
                        <div className="resident-va-notice-body">
                            <header>
                                <div className="resident-va-notice-header-left">
                                    <h3>{aviso.titulo || 'Aviso'}</h3>
                                    <span className={`notice-status-badge ${isRead ? 'read' : 'unread'}`}>
                                        {isRead ? '✓ Leído' : '● Pendiente'}
                                    </span>
                                </div>
                                {aviso.fechaPublicacion && (
                                    <span className="resident-va-notice-date">{formatearFechaCorta(aviso.fechaPublicacion)}</span>
                                )}
                            </header>
                            {aviso.descripcion && <p>{aviso.descripcion}</p>}
                            {aviso.categoria && <small className="resident-va-notice-category">{aviso.categoria}</small>}
                            <div className="resident-va-notice-footer">
                                <button
                                    type="button"
                                    className={`resident-va-read-btn ${isRead ? 'read' : 'unread'}`}
                                    onClick={() => onToggleRead(aviso.id, isRead)}
                                    disabled={isToggling}
                                >
                                    {isRead ? '✓ Leído (clic para desmarcar)' : 'Marcar como leído'}
                                </button>
                            </div>
                        </div>
                    </article>
                );
            })}
        </div>
    );
}

function obtenerTiempoVotacion(v) {
    if (!v) return 0;
    const val = v.fechaCreacion || v.fechaPublicacion;
    if (!val) return 0;
    if (typeof val.toMillis === 'function') return val.toMillis();
    if (typeof val.toDate === 'function') return val.toDate().getTime();
    if (val instanceof Date) return val.getTime();
    if (typeof val === 'number') return val;
    const parsed = new Date(val);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function normalizarFecha(valor) {
    if (!valor) return null;
    if (typeof valor.toDate === 'function') {
        return valor.toDate();
    }
    if (valor instanceof Date) {
        return valor;
    }
    const parsed = new Date(valor);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function inferTotalParticipantes(data = {}) {
    const candidates = [
        data.residentesActivos,
        data.totalResidentes,
        data.metaParticipantes,
        data.capacidadParticipacion,
    ];

    for (const value of candidates) {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0) {
            return Math.round(numeric);
        }
    }

    return 0;
}

function formatearFechaCorta(date) {
    return new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date);
}

function obtenerMetaAviso(categoria = '') {
    const normalized = categoria.toLowerCase();
    if (normalized.includes('importante') || normalized.includes('alerta')) {
        return { icon: <FaBullhorn />, className: 'is-warning' };
    }
    if (normalized.includes('evento')) {
        return { icon: <FaInfoCircle />, className: 'is-info' };
    }
    return { icon: <FaBell />, className: 'is-default' };
}
